import axios from 'axios';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const onLoginPage = window.location?.pathname === '/login';
      const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/employee-login') || url.includes('/auth/biometric-auth') || url.includes('/auth/system-login');

      if (!isAuthEndpoint && !onLoginPage) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        toast.error('Session expired. Please login again.');
      }
    } else if (error.response?.status === 403) {
      const url = error.config?.url || '';
      // List of endpoints that should silently fail for non-admin users
      const silentEndpoints = ['/holidays', '/employees/stats', '/employees', '/attendance/absent', '/attendance/present', '/attendance/late', '/leaves/balances', '/leaves/types'];
      const shouldShowToast = !silentEndpoints.some(endpoint => url.includes(endpoint));

      if (shouldShowToast) {
        toast.error(error.response?.data?.message || 'Access denied.');
      }
    } else if (error.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error('An error occurred. Please try again.');
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  getTenantNotice: (tenantId) => api.get('/auth/tenant-notice', { params: { tenantId } }),
  employeeLogin: (credentials) => api.post('/auth/employee-login', credentials),
  biometricAuth: (data) => api.post('/auth/biometric-auth', data),
  systemLogin: (credentials) => api.post('/auth/system-login', credentials),
  refreshToken: () => api.post('/auth/refresh'),
  getProfile: () => api.get('/auth/profile'),
  logout: () => api.post('/auth/logout'),
};

export const systemAPI = {
  getTenants: () => api.get('/system/tenants'),
  createTenant: (data) => api.post('/system/tenants', data),
  updateTenant: (id, data) => api.patch(`/system/tenants/${id}`, data),
  updateTenantStatus: (id, isActive) => api.patch(`/system/tenants/${id}/status`, { isActive }),
  getPackages: () => api.get('/system/packages'),
  createPackage: (data) => api.post('/system/packages', data),
  updatePackage: (id, data) => api.patch(`/system/packages/${id}`, data),
  updateTenantMobileManualPunch: (id, enabled) =>
    api.patch(`/system/tenants/${id}/mobile-manual-punch`, { enabled }),
  updateTenantSettings: (id, data) =>
    api.patch(`/system/tenants/${id}/settings`, data),
  updateTenantPackage: (id, packageId, options = {}) =>
    api.patch(`/system/tenants/${id}/package`, { packageId, ...options }),
  resetTenantAdminPassword: (id, newPassword) =>
    api.patch(`/system/tenants/${id}/admin-password`, { newPassword }),
  extendTenantSubscription: (id, data) =>
    api.patch(`/system/tenants/${id}/subscription/extend`, data),
};

// Employee API
export const employeesAPI = {
  getAll: () => api.get('/employees'),
  getById: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.patch(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  getProfile: () => api.get('/employees/profile'),
  updateProfile: (data) => api.patch('/employees/profile/update', data),
  changePassword: (data) => api.patch('/employees/change-password', data),
  getStats: () => api.get('/employees/stats'),
  // Departments
  listDepartments: () => api.get('/employees/departments'),
  createDepartment: (data) => api.post('/employees/departments', data),
  updateDepartment: (id, data) => api.patch(`/employees/departments/${id}`, data),
  deleteDepartment: (id) => api.delete(`/employees/departments/${id}`),
  // Positions
  listPositions: () => api.get('/employees/positions'),
  createPosition: (data) => api.post('/employees/positions', data),
  updatePosition: (id, data) => api.patch(`/employees/positions/${id}`, data),
  deletePosition: (id) => api.delete(`/employees/positions/${id}`),
};

// Attendance API
export const attendanceAPI = {
  biometricLogin: (data) => api.post('/attendance/biometric-login', data),
  manualLogin: (data) => api.post('/attendance/manual-login', data),
  markAttendance: (data) => api.post('/attendance/mark', data),
  markManualAttendance: (data) => api.post('/attendance/manual-mark', data),
  getTodayAttendance: (date) => api.get('/attendance/today', { params: { date } }),
  getStats: (date) => api.get('/attendance/stats', { params: { date } }),
  getReport: (startDate, endDate, employeeId) =>
    api.get('/attendance/report', {
      params: { startDate, endDate, employeeId }
    }),
  getByEmployee: (employeeId, startDate, endDate) =>
    api.get(`/attendance/employee/${employeeId}`, {
      params: { startDate, endDate }
    }),
  getMyAttendance: (startDate, endDate) =>
    api.get('/attendance/my-attendance', {
      params: { startDate, endDate }
    }),
  getAttendanceReport: (startDate, endDate, employeeId) =>
    api.get('/attendance/report', {
      params: { startDate, endDate, employeeId }
    }),
  updateAttendance: (id, data) => api.patch(`/attendance/${id}`, data),
  deleteAttendance: (id) => api.delete(`/attendance/${id}`),
  getMonthlyReport: (year, month, employeeId, dateSystem, startDate, endDate) =>
    api.get('/attendance/monthly-report', {
      params: { year, month, employeeId, dateSystem, startDate, endDate }
    }),
  exportToExcel: (startDate, endDate, employeeId) => {
    return api.get('/attendance/export/excel', {
      params: { startDate, endDate, employeeId },
      responseType: 'blob'
    });
  },
  exportMonthlyToExcel: (year, month, employeeId, dateSystem, startDate, endDate) => {
    return api.get('/attendance/export/monthly-excel', {
      params: { year, month, employeeId, dateSystem, startDate, endDate },
      responseType: 'blob'
    });
  },
  // Shifts
  listShifts: () => api.get('/attendance/shifts'),
  createShift: (data) => api.post('/attendance/shifts', data),
  updateShift: (id, data) => api.patch(`/attendance/shifts/${id}`, data),
  deleteShift: (id) => api.delete(`/attendance/shifts/${id}`),
  getAbsentEmployees: (date) => api.get('/attendance/absent', { params: { date } }),
  getPresentEmployees: (date) => api.get('/attendance/present', { params: { date } }),
  getLateEmployees: (date) => api.get('/attendance/late', { params: { date } }),
  autoCalculateWorkingHours: (date) => api.post('/attendance/auto-calculate-hours', { date }),
  startBreak: (employeeId) => api.post('/attendance/break/start', { employeeId }),
  endBreak: (employeeId) => api.post('/attendance/break/end', { employeeId }),
  getMobileManualAccess: () => api.get('/attendance/mobile-manual-access'),
};

// QR Attendance API (additive — separate from biometric/manual)
export const qrAttendanceAPI = {
  getSettings: () => api.get('/qr-attendance/settings'),
  updateSettings: (data) => api.patch('/qr-attendance/settings', data),
  getActiveCode: () => api.get('/qr-attendance/codes/active'),
  refreshCode: () => api.post('/qr-attendance/codes/refresh'),
  scan: (token) => api.post('/qr-attendance/scan', { token }),
  listScans: (limit = 50) => api.get('/qr-attendance/scans', { params: { limit } }),
};

// Holidays API
export const holidaysAPI = {
  getAllHolidays: () => api.get('/holidays'),
  getHolidaysWithNepali: () => api.get('/holidays/nepali'),
  getHolidaysForMonth: (year, month) =>
    api.get('/holidays/month', {
      params: { year, month }
    }),
  isHoliday: (date) =>
    api.get('/holidays/check', {
      params: { date }
    }),
  createHoliday: (data) => api.post('/holidays', data),
  updateHoliday: (id, data) => api.put(`/holidays/${id}`, data),
  deleteHoliday: (id) => api.delete(`/holidays/${id}`),
  getWeekendConfig: () => api.get('/holidays/weekends'),
  updateWeekendConfig: (days) => api.put('/holidays/weekends', { days }),
};

// Leaves API
export const leavesAPI = {
  getLeaveTypes: () => api.get('/leaves/types'),
  createLeaveType: (data) => api.post('/leaves/types', data),
  updateLeaveType: (id, data) => api.patch(`/leaves/types/${id}`, data),
  deleteLeaveType: (id) => api.delete(`/leaves/types/${id}`),

  bulkAssignLeaveBalances: (data) => api.post('/leaves/balances/bulk', data),
  updateLeaveBalance: (balanceId, leaveTypeId, totalDays) => api.patch(`/leaves/balances/${balanceId}/leave-type/${leaveTypeId}`, { totalDays }),
  getAllEmployeeBalances: (year) => api.get('/leaves/balances', { params: { year } }),
  getEmployeeLeaveBalances: (employeeId, year) => api.get(`/leaves/balances/${employeeId}`, { params: { year } }),
  getMyLeaveBalances: (year) => api.get('/leaves/my-balances', { params: { year } }),

  createLeaveApplication: (data) => api.post('/leaves/applications', data),
  getLeaveApplications: (employeeId, status) => api.get('/leaves/applications', { params: { employeeId, status } }),
  getMyLeaveApplications: (status) => api.get('/leaves/my-applications', { params: { status } }),
  updateLeaveApplication: (id, data) => api.patch(`/leaves/applications/${id}`, data),

  getLeaveCalendar: (startDate, endDate) => api.get('/leaves/calendar', { params: { startDate, endDate } }),
  getLeaveStats: (year) => api.get('/leaves/stats', { params: { year } }),
};

export default api;

export const jiraAPI = {
  getAuthUrl: () => api.get('/jira/auth/url'),
  getConfig: () => api.get('/jira/config'),
  getIssues: (q) => api.get('/jira/issues', { params: { q } }),
  addWorklog: (payload) => api.post('/jira/worklog/add', payload),
  getWorklogs: (issueKey) => api.get('/jira/worklog/list', { params: { issueKey } }),
  getMyWorklogs: (params) => api.get('/jira/worklog/my', { params }),
  getTeamWorklogs: (params) => api.get('/jira/worklog/team', { params }),
  getIssueMeta: (idOrKey) => api.get(`/jira/issue/${idOrKey}`),
  deleteWorklog: (worklogId, issueId) => api.delete(`/jira/worklog/${worklogId}`, { params: { issueId } }),
  updateWorklog: (worklogId, payload) => api.patch(`/jira/worklog/${worklogId}`, payload),
  updateApprovalStatus: (worklogId, status) => api.patch(`/jira/worklog/${worklogId}/approval`, { status }),

};
